import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { withShareLang } from '../../../utils/shareUrl';
import { useTheme } from '../../../context/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faUserPlus, faShareNodes, faPaperPlane, faLink } from '@fortawesome/free-solid-svg-icons';
import http from '../../../http-common';
import { toast } from 'react-toastify';
import { formatCompactNumber } from '../../../utils/numberFormatters';
import NewMessageModal from '../../common/Modals/NewMessageModal';
import RecommendUserModal from '../../common/Modals/RecommendUserModal';
import SocialShareModal from '../../EpkView/EpkSocialAction/SocialShareModal';

export default function UserSocialAction({ data, isEditMode, openLoginModal }) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const user = useSelector((state) => state.user);
  const userId = user?.id || '0';
  const [profileData, setProfileData] = useState(data);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => { setProfileData(data); }, [data]);

  const isOwnPage = String(userId) === String(data?._id);

  const shareUrl = withShareLang(`${window.location.origin}/user/${data?._id}`, i18n.language);
  const shareTitle = `Check out ${data?.firstName || ''} ${data?.lastName || ''}'s profile on KinoKlik!`;

  // One message per failure reason, so the user knows what actually went wrong.
  const showActionError = (type) => (err) => {
    const status = err.response?.status;
    // http-common already refreshes the session or sends the user to /login.
    if (status === 401) return;

    let message;
    if (!err.response) message = t("Could not reach KinoKlik. Check your connection and try again.");
    else if (status === 429) message = t("Too many shares from this connection. Please try again later.");
    else if (status === 404) message = t("This profile no longer exists.");
    else if (status === 400) message = t("You cannot do this on your own profile.");
    else if (type === 'likes') message = t("Could not update your like. Please try again.");
    else if (type === 'follow') message = t("Could not update your follow. Please try again.");
    else message = t("Could not count your share. You can still share the link.");
    toast.error(message);
  };

  const handleAction = (type) => {
    if (isEditMode) return;
    if (userId === '0') { openLoginModal(); return; }
    if (type === 'share') {
      if (!showShareModal) {
        http.post(`/users/share/${data._id}`).then((res) => {
          setProfileData(prev => ({ ...prev, sharings: res.data.sharings }));
        }).catch(showActionError('share'));
      }
      setShowShareModal(prev => !prev);
      return;
    }
    if (isOwnPage) return;

    switch (type) {
      case 'likes':
        http.post(`/users/like/${data._id}/${userId}`).then((res) => {
          setProfileData(prev => ({ ...prev, likes: res.data.likes }));
        }).catch(showActionError('likes'));
        break;
      case 'follow':
        http.post(`/users/follow/${data._id}/${userId}`).then((res) => {
          setProfileData(prev => ({ ...prev, kkFollowers: res.data.kkFollowers }));
        }).catch(showActionError('follow'));
        break;
      case 'message':
        setShowMessageModal(true);
        break;
      case 'recommend':
        setShowRecommendModal(true);
        break;
      default:
        break;
    }
  };

  const actions = [
    {
      key: 'likes',
      icon: faStar,
      count: profileData?.likes?.length || 0,
      label: t('Like'),
      tooltip: isOwnPage ? t('Your Likes') : t('Like Profile'),
      disabled: isOwnPage,
      hide: false,
    },
    {
      key: 'follow',
      icon: faUserPlus,
      count: profileData?.kkFollowers?.length || 0,
      label: t('Follow'),
      tooltip: isOwnPage ? t('Your Followers') : t('Follow'),
      disabled: isOwnPage,
      hide: false,
    },
    {
      key: 'recommend',
      icon: faLink,
      count: profileData?.recommendations || 0,
      label: t('Recommend'),
      tooltip: t('Recommend to Someone'),
      disabled: false,
      hide: userId === '0' || isOwnPage,
    },
    {
      key: 'message',
      icon: faPaperPlane,
      count: null,
      label: t('Message'),
      tooltip: t('Send Message'),
      disabled: false,
      hide: isOwnPage,
    },
    {
      key: 'share',
      icon: faShareNodes,
      count: profileData?.sharings || 0,
      label: t('Share'),
      tooltip: t('Share Profile'),
      disabled: false,
      hide: false,
    },
  ];

  return (
    <>
      <div className="tw-flex tw-flex-row tw-items-center tw-gap-3">
        {actions.map((action) =>
          !action.hide && (
            <div key={action.key} className="tw-relative tw-group">
              {action.key === 'share' && (
                <SocialShareModal
                  isOpen={showShareModal}
                  shareUrl={shareUrl}
                  shareTitle={shareTitle}
                  heading={t('Share this profile!')}
                  closeModal={() => setShowShareModal(false)}
                />
              )}
              {/* Desktop-only hover tooltip */}
              <div className="tw-pointer-events-none tw-absolute tw-bottom-[calc(100%+8px)] tw-left-1/2 -tw-translate-x-1/2 tw-whitespace-nowrap tw-rounded tw-bg-[#280D41] tw-border tw-border-[#712CB0]/40 tw-px-2 tw-py-1 tw-text-[11px] tw-text-white tw-opacity-0 tw-transition-opacity tw-duration-150 group-hover:tw-opacity-100 tw-hidden md:tw-block">
                {action.tooltip}
              </div>
              <button
                onClick={() => handleAction(action.key)}
                disabled={action.disabled}
                className={`tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-bg-transparent tw-border-none tw-p-0 tw-transition-all ${
                  action.disabled
                    ? 'tw-opacity-40 tw-cursor-default'
                    : 'tw-cursor-pointer'
                }`}
              >
                <div
                  className={`tw-flex tw-h-11 tw-w-11 md:tw-h-14 md:tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#712CB0]/40 tw-bg-[#280D41] tw-transition-all ${
                    !action.disabled
                      ? 'group-hover:tw-border-[#FF43A7] group-hover:tw-bg-[#712CB0]/20 group-hover:tw-scale-105'
                      : ''
                  }`}
                >
                  <FontAwesomeIcon
                    icon={action.icon}
                    className="tw-text-[#FF43A7] tw-text-base md:tw-text-lg"
                  />
                </div>
                {action.count !== null ? (
                  <span className={`tw-text-[10px] md:tw-text-xs tw-font-bold tw-leading-none ${isLight ? 'tw-text-[#111827]' : 'tw-text-white'}`}>
                    {formatCompactNumber(action.count)}
                  </span>
                ) : (
                  <span className="tw-text-[10px] md:tw-text-xs tw-font-bold tw-leading-none tw-invisible">&nbsp;</span>
                )}
              </button>
            </div>
          )
        )}
      </div>

      {showMessageModal && (
        <NewMessageModal
          close={() => setShowMessageModal(false)}
          userId={data?._id}
          userFirstName={data?.firstName}
          user={user}
          setRefresh={() => {}}
        />
      )}
      {showRecommendModal && (
        <RecommendUserModal
          close={() => setShowRecommendModal(false)}
          userId={data?._id}
          user={user}
          targetUser={data}
          onSuccess={() => setProfileData(prev => ({ ...prev, recommendations: (prev.recommendations || 0) + 1 }))}
        />
      )}
    </>
  );
}
